import { rowSlugs } from './slugs';

// ----- TEA (Techno-Economic Analysis) ------------------------------------
//
// The backend equation node spec accepts a DCFA TEA payload — same structure
// as DCFATEASpec.deserialize() expects. Every cost-shaped field is sent as a
// serialized Quantity ({scalar, unit: {numerator, denominator}}). Capacity
// allows a unit picker; the others have fixed units shown as static text.

const STRUCT_DOLLAR = { numerator: [{ name: 'dollar', species: null }], denominator: [] };
const STRUCT_DOLLAR_PER_YEAR = {
  numerator: [{ name: 'dollar', species: null }],
  denominator: [{ name: 'year', species: null }],
};
const STRUCT_SCALAR = { numerator: [], denominator: [] };

const buildQty = (scalar, unit) => ({ scalar: parseFloat(scalar) || 0, unit });

// Capacity units the user can pick from. Each maps to the structured
// Quantity unit format DCFATEASpec.deserialize() expects.
export const CAPACITY_UNITS = [
  { token: 'ton/day', label: 'ton/day', struct: { numerator: [{ name: 'ton', species: null }], denominator: [{ name: 'day', species: null }] } },
  { token: 'ton/year', label: 'ton/year', struct: { numerator: [{ name: 'ton', species: null }], denominator: [{ name: 'year', species: null }] } },
  { token: 'kg/day', label: 'kg/day', struct: { numerator: [{ name: 'kg', species: null }], denominator: [{ name: 'day', species: null }] } },
  { token: 'kg/year', label: 'kg/year', struct: { numerator: [{ name: 'kg', species: null }], denominator: [{ name: 'year', species: null }] } },
  { token: 'kg/hr', label: 'kg/hr', struct: { numerator: [{ name: 'kg', species: null }], denominator: [{ name: 'h', species: null }] } },
  { token: 'MW', label: 'MW', struct: { numerator: [{ name: 'MW', species: null }], denominator: [] } },
  { token: 'GW', label: 'GW', struct: { numerator: [{ name: 'GW', species: null }], denominator: [] } },
  { token: 'MWh/year', label: 'MWh/year', struct: { numerator: [{ name: 'MWh', species: null }], denominator: [{ name: 'year', species: null }] } },
  { token: 'GWh/year', label: 'GWh/year', struct: { numerator: [{ name: 'GWh', species: null }], denominator: [{ name: 'year', species: null }] } },
];

export const CAPACITY_UNIT_BY_TOKEN = Object.fromEntries(CAPACITY_UNITS.map(u => [u.token, u]));

// Species whose TEA cost basis is per-kWh rather than per-kg. `heat` belongs
// here alongside `electricity`: the structural validators already treat both
// as energy products when checking allocation dimensions, so costing heat
// per kg made the payload disagree with its own validation.
const ENERGY_SPECIES = new Set(['electricity', 'heat']);

export const teaCostUnitFor = species => (ENERGY_SPECIES.has(species) ? 'kWh' : 'kg');

export const isEnergyProduct = species => ENERGY_SPECIES.has(species);

// Capacity units that imply an energy/power reference product rather than a
// mass flow. Used to catch a reference product whose dimension disagrees with
// the capacity the user entered.
export const ENERGY_CAPACITY_UNITS = new Set(['MW', 'GW', 'MWh/year', 'GWh/year']);

// TEA addresses products by the same slug the backend gives the output's
// IOSpec — `SimpleTEA` resolves them with `node.output(cost_spec.product)`.
// That name comes from `_row_slugs`, so TEA keys have to be derived the
// same way every other slug in the builder is, not by a separate rule.
export const teaProductSlugs = outputs => rowSlugs(outputs);

// Build the structured `tea` payload that EquationNodeSpec.deserialize
// expects (matches DCFATEASpec.deserialize / SimpleTEASpec.deserialize wire
// format). Returns null when TEA is not configured.
export function buildTeaPayload(teaForm, outputs) {
  const mode = teaForm?.mode || 'none';
  if (mode === 'none') return null;

  const outputSlugs = teaProductSlugs(outputs);

  if (mode === 'simple') {
    const levelized = [];
    (outputs ?? []).forEach((io, idx) => {
      if (io.emissions) return;
      const slug = outputSlugs[idx];
      const cost = parseFloat(teaForm?.simple_costs?.[slug]);
      if (!Number.isFinite(cost)) return;
      levelized.push({
        product: slug,
        cost: buildQty(cost, {
          numerator: [{ name: 'dollar', species: null }],
          denominator: [{ name: teaCostUnitFor(io.species), species: null }],
        }),
      });
    });
    if (levelized.length === 0) return null;
    return {
      tea_type: 'simple',
      data: { levelized_costs: levelized, spec_type: 'process_only' },
    };
  }

  // DCFA
  if (!teaForm.reference_product) return null;
  const capUnit = CAPACITY_UNIT_BY_TOKEN[teaForm.reference_capacity_unit];
  if (!capUnit) return null;

  const refSlug = teaForm.reference_product;
  const refOutput = (outputs ?? [])[outputSlugs.indexOf(refSlug)];
  const varCostDenom = { name: teaCostUnitFor(refOutput?.species), species: null };

  const data = {
    reference_product: refSlug,
    reference_capacity: buildQty(teaForm.reference_capacity, capUnit.struct),
    capacity_factor: buildQty(teaForm.capacity_factor, STRUCT_SCALAR),
    capital_cost: buildQty(teaForm.capital_cost, STRUCT_DOLLAR),
    fixed_operating_costs: buildQty(teaForm.fixed_operating_costs, STRUCT_DOLLAR_PER_YEAR),
    fixed_maintenance_costs: buildQty(teaForm.fixed_maintenance_costs, STRUCT_DOLLAR_PER_YEAR),
    variable_operating_costs: buildQty(teaForm.variable_operating_costs, {
      numerator: [{ name: 'dollar', species: null }],
      denominator: [varCostDenom],
    }),
  };
  const exp = parseFloat(teaForm.scaling_exponent);
  if (Number.isFinite(exp)) {
    data.scaling_exponent = exp;
  }
  return { tea_type: 'dcfa', data };
}
